import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Button,
  IconButton,
  Chip,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  People as PeopleIcon,
  PostAdd as PostAddIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  AutoAwesome as AutoAwesomeIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  YouTube as YouTubeIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon,
  Add as AddIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  postsAPI,
  schedulingAPI,
  dashboardAPI,
} from '../services/api';
import { useClients } from '../contexts/ClientContext';
import { useNavigate } from 'react-router-dom';
import TokenStatusDialog from '../components/TokenStatusDialog';
import ActivationBanner from '../components/ActivationBanner';
import { SHOW_STANDALONE_INSTAGRAM_CONNECT } from '../config/featureFlags';
import { MetricCard } from '../components/primitives';

const PlatformStatusCard = ({ platform, onManage }) => {
  const platformConfig = {
    facebook: {
      icon: <FacebookIcon />,
      color: '#1877f2',
      name: 'Facebook',
      gradient: 'linear-gradient(135deg, #1877f2 0%, #166fe5 100%)',
    },
    instagram: {
      icon: <InstagramIcon />,
      color: '#E1306C',
      name: 'Instagram',
      gradient: 'linear-gradient(135deg, #E1306C 0%, #C13584 100%)',
    },
    linkedin: {
      icon: <LinkedInIcon />,
      color: '#0077b5',
      name: 'LinkedIn',
      gradient: 'linear-gradient(135deg, #0077b5 0%, #005885 100%)',
    },
    youtube: {
      icon: <YouTubeIcon />,
      color: '#ff0000',
      name: 'YouTube',
      gradient: 'linear-gradient(135deg, #ff0000 0%, #cc0000 100%)',
    },
    tumblr: {
      icon: <TwitterIcon />,
      color: '#00cf35',
      name: 'Tumblr',
      gradient: 'linear-gradient(135deg, #00cf35 0%, #00a82d 100%)',
    },
  };

  const config = platformConfig[platform] || {
    icon: null,
    color: '#8b949e',
    name: platform,
  };

  return (
    <Card
      sx={{
        background: 'linear-gradient(135deg, #161b22 0%, #21262d 100%)',
        border: '2px solid rgba(48, 54, 61, 0.5)',
        borderRadius: 4,
        boxShadow:
          '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        p: 0,
        m: 0,
        minHeight: 130,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          borderColor: config.color,
          transform: 'translateY(-2px)',
          boxShadow:
            '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
      }}
    >
      <CardContent
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          height: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #30363d 0%, #484f58 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              mr: 1.5,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {React.cloneElement(config.icon, { sx: { fontSize: 22 } })}
          </Box>
          <Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                color: '#f0f6fc',
                mb: 0.5,
                fontSize: '1rem',
                letterSpacing: '-0.01em',
              }}
            >
              {config.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#8b949e',
                fontWeight: 500,
                fontSize: '0.75rem',
              }}
            >
              Managed per client on Clients
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={onManage}
          sx={{
            background: config.gradient,
            color: 'white',
            px: 2,
            py: 0.5,
            fontWeight: 600,
            fontSize: '0.75rem',
            borderRadius: 2,
            boxShadow: `0 3px 10px ${config.color}40`,
            textTransform: 'none',
            minWidth: 'auto',
            '&:hover': {
              background: config.gradient,
              boxShadow: `0 4px 15px ${config.color}50`,
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          Manage
        </Button>
      </CardContent>
    </Card>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const { clients } = useClients();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalClients: 0,
    scheduledPosts: 0,
    totalPosts: 0,
    engagementRate: 0,
    todayScheduled: 0,
    weekScheduled: 0,
    schedulingEfficiency: 0,
    trendClients: null,
    trendScheduled: null,
    trendPosts: null,
    trendEngagement: null,
  });
  const [loading, setLoading] = useState(true);
  const [tokenStatusOpen, setTokenStatusOpen] = useState(false);

  const navigateToClientConnections = useCallback(() => {
    if (clients.length === 1) {
      navigate(`/clients?client_id=${clients[0].id}`);
      return;
    }
    navigate('/clients');
  }, [clients, navigate]);

  useEffect(() => {
    const toArray = (res) => {
      if (Array.isArray(res)) return res;
      if (res && Array.isArray(res.data)) return res.data;
      if (res && Array.isArray(res.clients)) return res.clients;
      if (res && Array.isArray(res.posts)) return res.posts;
      return [];
    };

    const fetchStats = async () => {
      try {
        const [dashboardRes, postsRes, schedulingRes] = await Promise.allSettled([
          dashboardAPI.getStats(),
          postsAPI.getPosts(),
          schedulingAPI.getSchedulingStats(),
        ]);

        const dashboardOk = dashboardRes.status === 'fulfilled' && dashboardRes.value && typeof dashboardRes.value.total_clients === 'number';
        const useDashboard = dashboardOk && (dashboardRes.value.total_clients > 0 || clients.length === 0);

        if (useDashboard) {
          const d = dashboardRes.value;
          setStats({
            totalClients: d.total_clients ?? 0,
            scheduledPosts: d.scheduled_posts ?? 0,
            totalPosts: (d.total_posts ?? d.total_posts_published_this_month) ?? 0,
            engagementRate: d.avg_engagement ?? 0,
            todayScheduled: 0,
            weekScheduled: 0,
            schedulingEfficiency: 0,
            trendClients: d.trend_clients ?? null,
            trendScheduled: d.trend_scheduled ?? null,
            trendPosts: d.trend_posts ?? null,
            trendEngagement: d.trend_engagement ?? null,
          });
        } else {
          const posts = toArray(postsRes.status === 'fulfilled' ? postsRes.value : null);
          const schedulingStats = schedulingRes.status === 'fulfilled' && schedulingRes.value && typeof schedulingRes.value === 'object' ? schedulingRes.value : {};
          const scheduledPosts = posts.filter((p) => p && p.status === 'scheduled');
          const now = new Date();
          const firstDayThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          const publishedAll = posts.filter((p) => p && p.status === 'published');
          const publishedThisMonth = publishedAll.filter(
            (p) => p.published_time && new Date(p.published_time) >= firstDayThisMonth
          );
          const totalEngagement = publishedThisMonth.reduce((sum, p) => {
            const m = (p && p.engagement_metrics) || {};
            return sum + (m.likes || 0) + (m.comments || 0) + (m.shares || 0);
          }, 0);
          const avgEngagement = publishedThisMonth.length > 0 ? Math.round(totalEngagement / publishedThisMonth.length) : 0;
          setStats({
            totalClients: clients.length,
            scheduledPosts: schedulingStats.total_scheduled ?? scheduledPosts.length,
            totalPosts: publishedAll.length,
            engagementRate: avgEngagement,
            todayScheduled: 0,
            weekScheduled: 0,
            schedulingEfficiency: 0,
            trendClients: null,
            trendScheduled: null,
            trendPosts: null,
            trendEngagement: null,
          });
        }

      } catch (err) {
        console.error('Dashboard stats failed:', err);
        setStats((prev) => ({ ...prev, totalClients: 0, scheduledPosts: 0, totalPosts: 0, engagementRate: 0 }));
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [clients]);

  const refreshDashboardStats = useCallback(async () => {
    setLoading(true);
    try {
      const data = await dashboardAPI.getStats();
      setStats({
        totalClients: data.total_clients ?? 0,
        scheduledPosts: data.scheduled_posts ?? 0,
        totalPosts: (data.total_posts ?? data.total_posts_published_this_month) ?? 0,
        engagementRate: data.avg_engagement ?? 0,
        todayScheduled: 0,
        weekScheduled: 0,
        schedulingEfficiency: 0,
        trendClients: data.trend_clients ?? null,
        trendScheduled: data.trend_scheduled ?? null,
        trendPosts: data.trend_posts ?? null,
        trendEngagement: data.trend_engagement ?? null,
      });
    } catch (err) {
      try {
        const [postsRes, schedulingRes] = await Promise.all([
          postsAPI.getPosts(),
          schedulingAPI.getSchedulingStats(),
        ]);
        const posts = Array.isArray(postsRes) ? postsRes : [];
        const schedulingStats = schedulingRes && typeof schedulingRes === 'object' ? schedulingRes : {};
        const scheduledPosts = posts.filter((p) => p.status === 'scheduled');
        const now = new Date();
        const firstDayThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const publishedAll = posts.filter((p) => p.status === 'published');
        const publishedThisMonth = publishedAll.filter(
          (p) => p.published_time && new Date(p.published_time) >= firstDayThisMonth
        );
        const totalEngagement = publishedThisMonth.reduce((sum, p) => {
          const m = p.engagement_metrics || {};
          return sum + (m.likes || 0) + (m.comments || 0) + (m.shares || 0);
        }, 0);
        const avgEngagement = publishedThisMonth.length > 0 ? Math.round(totalEngagement / publishedThisMonth.length) : 0;
        setStats({
          totalClients: clients.length,
          scheduledPosts: schedulingStats.total_scheduled ?? scheduledPosts.length,
          totalPosts: publishedAll.length,
          engagementRate: avgEngagement,
          todayScheduled: 0,
          weekScheduled: 0,
          schedulingEfficiency: 0,
          trendClients: null,
          trendScheduled: null,
          trendPosts: null,
          trendEngagement: null,
        });
      } catch (e) {
        console.error('Refresh stats failed:', e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <Box>
      {/* Enhanced Header Section */}
      <Box sx={{ mb: 5 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: '#f0f6fc',
                mb: 1,
                fontSize: '2.5rem',
                letterSpacing: '-0.02em',
                background: 'linear-gradient(135deg, #f0f6fc 0%, #c9d1d9 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Welcome back, {user?.username || 'User'}! 👋
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#8b949e',
                fontSize: '1.1rem',
                fontWeight: 500,
              }}
            >
              Here's what's happening with your social media campaigns today
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Token Status" arrow>
              <IconButton
                onClick={() => setTokenStatusOpen(true)}
                sx={{
                  backgroundColor: 'rgba(247, 129, 102, 0.1)',
                  border: '1px solid rgba(247, 129, 102, 0.2)',
                  color: '#f78166',
                  width: 48,
                  height: 48,
                  '&:hover': {
                    backgroundColor: 'rgba(247, 129, 102, 0.2)',
                    color: '#f78166',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <SecurityIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh Data" arrow>
              <IconButton
                onClick={refreshDashboardStats}
                sx={{
                  backgroundColor: 'rgba(88, 166, 255, 0.1)',
                  border: '1px solid rgba(88, 166, 255, 0.2)',
                  color: '#8b949e',
                  width: 48,
                  height: 48,
                  '&:hover': {
                    backgroundColor: 'rgba(88, 166, 255, 0.2)',
                    color: '#f0f6fc',
                    transform: 'rotate(180deg)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <ActivationBanner />

      <Grid container spacing={4} sx={{ mb: 6, mt: 1 }}>
        <Grid item xs={12} sm={6} md={6} lg={6}>
          <MetricCard
            title="Total Clients"
            value={stats.totalClients}
            subtitle="Active accounts"
            icon={<PeopleIcon />}
            accentColor="#58a6ff"
            trend={stats.trendClients ?? undefined}
            loading={loading}
            variant="featured"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={6}>
          <MetricCard
            title="Scheduled Posts"
            value={stats.scheduledPosts}
            subtitle="Ready to publish"
            icon={<ScheduleIcon />}
            accentColor="#f78166"
            trend={stats.trendScheduled ?? undefined}
            loading={loading}
            variant="featured"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={6}>
          <MetricCard
            title="Total Posts"
            value={stats.totalPosts}
            subtitle="All time"
            icon={<PostAddIcon />}
            accentColor="#238636"
            trend={stats.trendPosts ?? undefined}
            loading={loading}
            variant="featured"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={6}>
          <MetricCard
            title="Avg Engagement"
            value={stats.engagementRate}
            subtitle="Likes, comments, shares"
            icon={<TrendingUpIcon />}
            accentColor="#d29922"
            trend={stats.trendEngagement ?? undefined}
            loading={loading}
            variant="featured"
          />
        </Grid>
      </Grid>

      <Box sx={{ mb: 6 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#f0f6fc',
              fontSize: '1.75rem',
            }}
          >
            Platform Connections
          </Typography>
          <Button
            variant="outlined"
            onClick={navigateToClientConnections}
            sx={{ borderColor: '#58a6ff', color: '#58a6ff' }}
          >
            Open Clients
          </Button>
        </Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          Platform accounts are connected per workspace (client). Use the Clients
          page to connect Facebook (Instagram feed posts go out via a linked Facebook
          Page), LinkedIn, YouTube, and other platforms for the correct client.
        </Alert>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Box sx={{ flex: '1 1 320px', minWidth: 320, maxWidth: 400 }}>
            <PlatformStatusCard
              platform="facebook"
              onManage={navigateToClientConnections}
            />
          </Box>
          {SHOW_STANDALONE_INSTAGRAM_CONNECT && (
            <Box sx={{ flex: '1 1 320px', minWidth: 320, maxWidth: 400 }}>
              <PlatformStatusCard
                platform="instagram"
                onManage={navigateToClientConnections}
              />
            </Box>
          )}
          <Box sx={{ flex: '1 1 320px', minWidth: 320, maxWidth: 400 }}>
            <PlatformStatusCard
              platform="linkedin"
              onManage={navigateToClientConnections}
            />
          </Box>
          <Box sx={{ flex: '1 1 320px', minWidth: 320, maxWidth: 400 }}>
            <PlatformStatusCard
              platform="youtube"
              onManage={navigateToClientConnections}
            />
          </Box>
          <Box sx={{ flex: '1 1 320px', minWidth: 320, maxWidth: 400 }}>
            <PlatformStatusCard
              platform="tumblr"
              onManage={navigateToClientConnections}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ mb: 5 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#f0f6fc',
            mb: 4,
            fontSize: '1.75rem',
          }}
        >
          Quick Actions
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box sx={{ flex: '1 1 400px', minWidth: 400, maxWidth: 600 }}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #161b22 0%, #21262d 100%)',
                border: '1px solid rgba(48, 54, 61, 0.5)',
                borderRadius: 4,
                boxShadow:
                  '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    'linear-gradient(45deg, rgba(88, 166, 255, 0.05) 0%, transparent 50%, rgba(88, 166, 255, 0.02) 100%)',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  pointerEvents: 'none',
                },
                '&:hover': {
                  transform: 'translateY(-6px)',
                  borderColor: '#58a6ff',
                  boxShadow:
                    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                  '&::before': {
                    opacity: 1,
                  },
                },
                p: 0,
                m: 0,
                height: '100%',
                width: '100%',
              }}
            >
              <CardContent
                sx={{
                  p: 4,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 3,
                      background:
                        'linear-gradient(135deg, #58a6ff 0%, #1f6feb 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(88, 166, 255, 0.3)',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: -2,
                        left: -2,
                        right: -2,
                        bottom: -2,
                        background: 'linear-gradient(135deg, #58a6ff, #1f6feb)',
                        borderRadius: 3,
                        zIndex: -1,
                        opacity: 0.3,
                      },
                    }}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: 28 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: '#f0f6fc',
                        fontSize: '1.4rem',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      Generate AI Content
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#8b949e',
                        fontSize: '1rem',
                        fontWeight: 500,
                      }}
                    >
                      Create engaging posts with AI
                    </Typography>
                  </Box>
                  <ArrowForwardIcon sx={{ color: '#58a6ff', fontSize: 24 }} />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#6e7681',
                    fontSize: '1rem',
                    lineHeight: 1.6,
                    mb: 3,
                  }}
                >
                  Use our AI to generate compelling social media content
                  tailored to your clients' brands and audiences.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/ai-content')}
                  sx={{
                    background:
                      'linear-gradient(135deg, #58a6ff 0%, #1f6feb 100%)',
                    boxShadow: '0 4px 12px rgba(88, 166, 255, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #79c0ff 0%, #58a6ff 100%)',
                      boxShadow: '0 6px 20px rgba(88, 166, 255, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                    borderRadius: 2,
                    px: 2.5,
                    py: 1.25,
                    fontWeight: 600,
                  }}
                >
                  Create Content
                </Button>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 400px', minWidth: 400, maxWidth: 600 }}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #161b22 0%, #21262d 100%)',
                border: '1px solid rgba(48, 54, 61, 0.5)',
                borderRadius: 4,
                boxShadow:
                  '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    'linear-gradient(45deg, rgba(247, 129, 102, 0.05) 0%, transparent 50%, rgba(247, 129, 102, 0.02) 100%)',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  pointerEvents: 'none',
                },
                '&:hover': {
                  transform: 'translateY(-4px)',
                  borderColor: '#f78166',
                  boxShadow:
                    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                  '&::before': {
                    opacity: 1,
                  },
                },
                p: 0,
                m: 0,
                height: '100%',
                width: '100%',
              }}
            >
              <CardContent
                sx={{
                  p: 4,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 3,
                      background:
                        'linear-gradient(135deg, #f78166 0%, #d15704 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(247, 129, 102, 0.3)',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: -2,
                        left: -2,
                        right: -2,
                        bottom: -2,
                        background: 'linear-gradient(135deg, #f78166, #d15704)',
                        borderRadius: 3,
                        zIndex: -1,
                        opacity: 0.3,
                      },
                    }}
                  >
                    <ScheduleIcon sx={{ fontSize: 28 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: '#f0f6fc',
                        fontSize: '1.4rem',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      Schedule Posts
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#8b949e',
                        fontSize: '1rem',
                        fontWeight: 500,
                      }}
                    >
                      Plan your content calendar
                    </Typography>
                  </Box>
                  <ArrowForwardIcon sx={{ color: '#f78166', fontSize: 24 }} />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#6e7681',
                    fontSize: '1rem',
                    lineHeight: 1.6,
                    mb: 3,
                  }}
                >
                  Schedule posts across multiple platforms and clients with our
                  advanced scheduling system.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/posts')}
                  sx={{
                    background:
                      'linear-gradient(135deg, #f78166 0%, #d15704 100%)',
                    boxShadow: '0 4px 12px rgba(247, 129, 102, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #ff9a7a 0%, #f78166 100%)',
                      boxShadow: '0 6px 20px rgba(247, 129, 102, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                    borderRadius: 3,
                    px: 3,
                    py: 1.5,
                    fontWeight: 700,
                  }}
                >
                  Schedule Now
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>

      {/* Token Status Dialog */}
      <TokenStatusDialog
        open={tokenStatusOpen}
        onClose={() => setTokenStatusOpen(false)}
      />
    </Box>
  );
}
